import { Button, Group, Stack, Textarea, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect } from "react";
import { ContentSubmissionItem } from "../../../../../../gql/__generated__/graphql";
import { isValidUrl } from "../../../../../utils/dataUtils";

export interface SubmitContentFormValues {
  videoUrls: string[];
  message: string;
}

interface SubmitContentFormProps {
  numberOfVideos: number;
  onSubmit: (data: {
    contentItems: ContentSubmissionItem[];
    message?: string;
  }) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

function SubmitContentForm({
  numberOfVideos,
  onSubmit,
  onCancel,
  isLoading = false,
}: SubmitContentFormProps) {
  const form = useForm<SubmitContentFormValues>({
    initialValues: {
      videoUrls: Array(numberOfVideos).fill(""),
      message: "",
    },
    validate: {
      videoUrls: (value) => {
        // Check if all URLs are filled and valid
        for (let i = 0; i < value.length; i++) {
          const url = value[i];
          if (!url.trim()) {
            return `Video URL ${i + 1} is required`;
          }
          if (!isValidUrl(url)) {
            return `Video URL ${i + 1} must be a valid URL`;
          }
        }
        return null;
      },
    },
  });

  // Reset form when numberOfVideos changes
  useEffect(() => {
    form.setValues({
      videoUrls: Array(numberOfVideos).fill(""),
      message: "",
    });
  }, [numberOfVideos]);

  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...form.values.videoUrls];
    newUrls[index] = value;
    form.setFieldValue("videoUrls", newUrls);
  };

  const getUrlError = (index: number) => {
    const url = form.values.videoUrls[index];
    if (!url.trim()) {
      return "This field is required";
    }
    if (url.trim() && !isValidUrl(url)) {
      return "Please enter a valid URL";
    }
    return null;
  };

  const handleSubmit = async (values: SubmitContentFormValues) => {
    const contentItems: ContentSubmissionItem[] = values.videoUrls.map(
      (url) => ({
        contentUrl: url,
        contentType: "VIDEO",
      })
    );

    await onSubmit({
      contentItems,
      message: values.message || undefined,
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {Array.from({ length: numberOfVideos }).map((_, index) => (
          <TextInput
            key={index}
            label={`Video URL ${index + 1}`}
            placeholder="https://www.tiktok.com/..."
            value={form.values.videoUrls[index] || ""}
            onChange={(e) => handleUrlChange(index, e.target.value)}
            error={form.errors.videoUrls ? getUrlError(index) : null}
            required
          />
        ))}

        <Textarea
          label="Message (optional)"
          placeholder="Add a message to the brand..."
          {...form.getInputProps("message")}
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            Submit Content
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export default SubmitContentForm;
